import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { MessageCircle, Plus, Trash2, AlertCircle, Check } from "lucide-react";

const CATEGORY_TONE = {
  utility: "pm-pill-blue",
  marketing: "pm-pill-amber",
  authentication: "pm-pill-violet",
};

const STATUS_TONE = {
  draft: "pm-pill-slate",
  submitted: "pm-pill-amber",
  approved: "pm-pill-green",
  rejected: "pm-pill-red",
  submission_failed: "pm-pill-red",
};

export default function WhatsAppTemplates() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const refresh = () => api.get("/comms/whatsapp-templates").then((r) => setItems(r.data || []));
  useEffect(() => { refresh(); }, []);
  const flash = (m) => { setToast(m); setTimeout(() => setToast(null), 2500); };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this template? It will be removed from Twilio if it was submitted.")) return;
    await api.delete(`/comms/whatsapp-templates/${id}`);
    flash("Template deleted");
    refresh();
  };

  return (
    <Layout title="WhatsApp templates" subtitle="Meta-approved messages for proactive outbound WhatsApp">
      <div className="max-w-5xl space-y-5">
        <div className="flex items-start gap-3 p-4 rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200">
          <MessageCircle size={18} className="text-teal-700 mt-1 shrink-0" />
          <div className="flex-1">
            <div className="font-display text-lg font-bold tracking-tight">How templates work</div>
            <p className="text-sm text-slate-700 mt-1 leading-relaxed">
              WhatsApp requires Meta-approved templates for any message sent outside the 24-hour customer-service window. Build them here once — Playmaker submits to Twilio Content API, Meta approves within 24-72 hours, then you can fire them from any inbox thread or Travel Planner reminder.
            </p>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="pm-btn-primary h-10 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5"
            data-testid="wa-new-template"
          ><Plus size={13} /> New template</button>
        </div>

        <div className="pm-card overflow-hidden" data-testid="wa-templates-list">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Name</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Category</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Body</th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan="5" className="p-8 text-center text-slate-500 text-sm">
                  No templates yet. Click <b>New template</b> above to create your first one — typically a contract-renewal reminder or fixture alert.
                </td></tr>
              )}
              {items.map((t) => (
                <tr key={t.id} className="border-b border-slate-100" data-testid={`wa-template-${t.id}`}>
                  <td className="px-4 py-3 font-bold">{t.friendly_name}</td>
                  <td className="px-4 py-3"><span className={`pm-pill ${CATEGORY_TONE[t.category] || "pm-pill-slate"}`}>{t.category}</span></td>
                  <td className="px-4 py-3 text-slate-700 text-[13px] max-w-md truncate" title={t.body_text}>{t.body_text}</td>
                  <td className="px-4 py-3"><span className={`pm-pill ${STATUS_TONE[t.approval_status] || "pm-pill-slate"} text-[10px]`}>
                    {t.approval_status === "approved" ? <><Check size={9} className="inline mr-1" />approved</>
                      : t.approval_status === "submission_failed" ? <><AlertCircle size={9} className="inline mr-1" />failed</>
                      : t.approval_status}
                  </span></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onDelete(t.id)} className="text-red-700 hover:bg-red-50 p-1 rounded-lg" data-testid={`wa-template-delete-${t.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && <NewTemplateDrawer onClose={() => setOpen(false)} onDone={() => { setOpen(false); refresh(); flash("Template submitted to Twilio for Meta approval"); }} />}
      {toast && <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white text-sm px-4 py-3 rounded-xl shadow-2xl" data-testid="wa-toast">{toast}</div>}
    </Layout>
  );
}

function NewTemplateDrawer({ onClose, onDone }) {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("utility");
  const [body, setBody] = useState("");
  const [vars, setVars] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    setErr(null); setBusy(true);
    try {
      if (!name.trim() || !body.trim()) throw new Error("Name and body are required");
      await api.post("/comms/whatsapp-templates", {
        friendly_name: name.trim(),
        category, language: "en",
        body_text: body.trim(),
        variables_help: vars.trim() || null,
      });
      onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-testid="wa-template-drawer">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Create</div>
          <div className="font-display text-lg font-bold tracking-tight">New WhatsApp template</div>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Friendly name (lowercase, no spaces)</span>
            <input className="pm-input mt-1" value={name} onChange={(e) => setName(e.target.value.toLowerCase().replace(/\s+/g, "_"))} placeholder="contract_expiry_reminder" data-testid="wa-tpl-name" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Category</span>
            <select className="pm-input mt-1" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="wa-tpl-category">
              <option value="utility">Utility (transactional reminders, alerts)</option>
              <option value="marketing">Marketing (promotional content — higher Meta scrutiny)</option>
              <option value="authentication">Authentication (OTPs only)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Body</span>
            <textarea className="pm-input mt-1" rows={4} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder="Hi {{1}}, this is a reminder from {{2}} that your contract with {{3}} expires on {{4}}."
              data-testid="wa-tpl-body" />
            <span className="text-[10px] text-slate-500">Use {"{{1}}, {{2}}, {{3}}"} for variables. Meta does not allow URLs or marketing CTAs in Utility templates.</span>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Variables legend (optional, for your team)</span>
            <input className="pm-input mt-1" value={vars} onChange={(e) => setVars(e.target.value)} placeholder="1=player_name, 2=agent_name, 3=club_name, 4=expiry_date" data-testid="wa-tpl-vars" />
          </label>
          {err && <div className="text-xs text-red-700 flex items-center gap-1.5" data-testid="wa-tpl-error"><AlertCircle size={12} /> {err}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex items-center gap-2 bg-slate-50">
          <button onClick={onClose} className="pm-btn-secondary h-10 px-4 rounded-xl text-sm font-bold">Cancel</button>
          <div className="flex-1" />
          <button onClick={submit} disabled={busy} className="pm-btn-primary h-10 px-5 rounded-xl text-sm font-bold disabled:opacity-40" data-testid="wa-tpl-submit">
            {busy ? "Submitting…" : "Submit to Twilio"}
          </button>
        </div>
      </div>
    </div>
  );
}
